import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Button, 
  Row, 
  Col, 
  Space,
  Divider,
  Form,
  Input,
  Select,
  DatePicker,
  Radio,
  Modal,
  Table,
  ColorPicker,
  Pagination,
  message
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined } from '@ant-design/icons';
import AdminLayout from '../../../components/Layouts/AdminLayout/AdminLayout';
import dayjs from 'dayjs';
import { adminLearningScheduleApi, adminUnitApi, adminTextbookApi, academyStudentApi } from '@/services/api';
import StudentTable from '../components/table/StudentTable';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface LearningEventDetail {
  id: string;
  title: string;
  start: string;
  end: string;
  studentName: string;
  studentId: string;
  birthDate: string;
  academy: string;
  description?: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  backgroundColor?: string;
  borderColor?: string;
  textbook?: string;
  unit?: string;
  maxUnitsPerDay?: number;
  scoring?: 'yes' | 'no';
  registrant?: string;
  registrationDate?: string;
  groupUuid?: string;
}

interface Student {
  id: string;
  name: string;
  academy: string;
  grade: string;
  status: 'active' | 'inactive';
  no: number;
  academyCode: string;
  studentName: string;
  birthDate: string;
}

interface Textbook {
  id: string;
  name: string;
  publisher: string;
}

interface Unit {
  id: string;
  name: string;
  textbookId?: string;
  textbookName?: string;
  unitCategory?: string;
}

const AdminLearningDetail: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [eventDetail, setEventDetail] = useState<LearningEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // 항상 수정모드로 동작
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [isStudentModalVisible, setIsStudentModalVisible] = useState(false);
  const [selectedTextbook, setSelectedTextbook] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('#1890ff');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedUnitsState, setSelectedUnitsState] = useState<Unit[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  // 샘플 이벤트 데이터 (실제로는 API에서 가져올 데이터)
  // 실데이터는 API에서 로드



  const [students, setStudents] = useState<Student[]>([]);

  // 교재 더미 데이터
  const [textbooks] = useState<Textbook[]>([]);

  // 수정화면: 유닛 추가/변경 불가, 로컬 유닛 목록 상태 미사용

  // 선택된 교재/유닛 정보를 테이블로 표시합니다

  // 유닛 목록 테이블용 더미 데이터


  const maxUnitsPerDayOptions = Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: `${i + 1}개` }));

  const scoringOptions = [
    { value: 'yes', label: '채점함' },
    { value: 'no', label: '채점하지 않음' },
  ];

  const typeOptions = useMemo(() => ([
    { id: '파닉스데이', name: '파닉스 데이' },
    { id: '스타터_파닉스', name: '스타터 파닉스' },
    { id: '스타터_뉘앙스', name: '스타터 뉘앙스' },
    { id: '스타터_스트럭처', name: '스타터 스트럭처' },
    { id: '에세이', name: '에세이' },
    { id: '통번역', name: '통번역' },
    { id: '리딩북', name: '리딩북' },
  ]), []);

    useEffect(() => {
    (async () => {
      try {
        const res = await adminLearningScheduleApi.detail(String(eventId));
        const data = res?.data || res;
        const d = data?.data || data;
        const event = {
          id: String(d.id),
          title: d.title || '',
          start: d.start,
          end: d.end,
          studentName: '',
          studentId: String(d.studentUserId || ''),
          birthDate: '',
          academy: String(d.academyId || ''),
          status: 'scheduled',
          backgroundColor: d.backgroundColor || '#1890ff',
          borderColor: d.borderColor || d.backgroundColor || '#1890ff',
          groupUuid: d.groupUuid || d.group_uuid || '',
        } as LearningEventDetail;
        setEventDetail(event);
        setSelectedTextbook('');
        setSelectedColor(event.backgroundColor || '#1890ff');
        form.setFieldsValue({
          learningName: event.title,
          dateRange: event.start && event.end ? [dayjs(event.start), dayjs(event.end)] : undefined,
          calendarColor: event.backgroundColor,
          scoring: 'yes',
          registrant: '관리자',
          registrationDate: dayjs().format('YYYY-MM-DD'),
          maxUnitsPerDay: d.maxUnitsPerDay || 1,
        });

        // 학습 대상자 로드: 같은 학습 + 동일 기간(시작/종료 동일) 학생만 표시
        try {
          // [Modified] 그룹 ID 기반으로 동일 학습 학생 목록 조회
          const sameRes = await adminLearningScheduleApi.listStudentsInSameLearning(String(eventId));
          const sameData = sameRes?.data || sameRes;
          const studentIdsRaw = sameData?.studentUserIds || sameData?.data?.studentUserIds || [];
          const studentIds: string[] = studentIdsRaw.map((id: any) => String(id));

          const academyCodeForStudent = String(d.academyCode || d.academyId || '');
          const resStudents = await academyStudentApi.list({ academyCode: academyCodeForStudent, page: 0, size: 1000 });
          const sd = resStudents?.data || resStudents; const p = sd?.data || sd; const content = p?.content || [];

          let all: any[] = content
            .filter((s: any) => studentIds.includes(String(s.userId)))
            .map((s: any, idx: number) => ({
              id: String(s.userId),
              no: idx + 1,
              academyCode: s.academyCode || academyCodeForStudent,
              studentName: s.name,
              birthDate: s.birthDate
            }));

          if (all.length < studentIds.length) {
            const foundIds = all.map(s => s.id);
            const missingIds = studentIds.filter(id => !foundIds.includes(id));
            const missingStudents = missingIds.map((sid: string, i: number) => ({
              id: sid,
              no: all.length + i + 1,
              academyCode: academyCodeForStudent,
              studentName: `학생 ID: ${sid}`,
              birthDate: ''
            }));
            all = [...all, ...missingStudents];
          }

          setStudents(all as any);
          setSelectedStudents(all as any);
        } catch (error) {
          console.error('Error loading students with same period:', error);
        }

        // 유형/유닛: schedule에 연결된 유닛 기반으로 표시
        try {
          const unitList = d.units || [];  // learning_schedule_unit에서 가져온 유닛 목록
          if (unitList.length) {
            const textbookNameMap: Record<string, string> = {};
            const textbookIdCandidates = unitList
              .map((x: any) => x.textbookId)
              .filter((id: unknown): id is number => typeof id === 'number');
            const uniqueTextbookIds = Array.from(new Set<number>(textbookIdCandidates));
            if (uniqueTextbookIds.length) {
              await Promise.all(uniqueTextbookIds.map(async (textbookId) => {
                try {
                  const tr = await adminTextbookApi.detail(String(textbookId));
                  const td = tr?.data || tr; const body = td?.data || td || {};
                  const name = body.name || body.title || '-';
                  textbookNameMap[String(textbookId)] = name;
                } catch (_) {
                  textbookNameMap[String(textbookId)] = '-';
                }
              }));
            }

            const details = await Promise.all(unitList.map(async (x: any) => {
              const unitId = x.id || x.unitId;
              let unitTitle = `유닛 #${unitId}`;
              let unitCategory = '-';
              const linkTextbookId = x.textbookId != null ? String(x.textbookId) : undefined;
              let textbookName = linkTextbookId ? textbookNameMap[linkTextbookId] : undefined;
              try {
                const ur = await adminUnitApi.detail(String(unitId));
                const ud = ur?.data || ur; const body = ud?.data || ud;
                unitTitle = body.title || body.name || unitTitle;
                unitCategory = body.unitCategory || unitCategory;
              } catch {}
              if (!textbookName) {
                try {
                  const tr = await adminTextbookApi.findByUnit(String(unitId));
                  const td = tr?.data || tr; const tlist = td?.data || td || [];
                  if (Array.isArray(tlist) && tlist.length) {
                    textbookName = tlist[0].name || '-';
                  }
                } catch {}
              }
              return {
                id: String(unitId),
                name: unitTitle,
                textbookId: linkTextbookId,
                textbookName: textbookName || '-',
                unitCategory
              } as Unit;
            }));
            setSelectedUnitsState(details);
            if (details.length) {
              form.setFieldsValue({ type: details[0]?.unitCategory || undefined });
            }
          }
        } catch (error) {
          console.error('Error loading units:', error);
        }

      } catch (e) {
        setEventDetail(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, form, typeOptions]);

  // 상태/포맷 유틸은 API 연결 후 필요 시 재도입

  // 수정화면에서는 유형/교재/유닛 추가 불가

  // 수정화면에서는 유형/교재/유닛 추가 불가



  const handleStudentModalOk = () => {
    // 체크박스로 선택된 학생들을 추가
    const selectedStudentData = students.filter(student => 
      selectedRowKeys.includes(student.id)
    );
    
    // 중복 제거하면서 추가
    selectedStudentData.forEach(student => {
      if (!selectedStudents.find(s => s.id === student.id)) {
        setSelectedStudents(prev => [...prev, student]);
      }
    });
    
    setIsStudentModalVisible(false);
    setSelectedRowKeys([]); // 선택 초기화
  };

  const handleStudentModalCancel = () => {
    setIsStudentModalVisible(false);
  };

  const handleRowSelectionChange = (selectedKeys: React.Key[]) => {
    setSelectedRowKeys(selectedKeys);
  };

  const handleRemoveStudent = (studentId: string) => {
    setSelectedStudents((prev) => prev.filter((s) => s.id !== studentId));
    const removedCurrent = eventDetail && String(eventDetail.studentId) === String(studentId);
    if (removedCurrent) {
      message.info('현재 편집 중인 학생의 학습이 삭제되어 목록으로 이동합니다.');
      navigate('/admin/learning');
    }
  };

  // 편집 토글은 현재 즉시 편집 모드 유지

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!eventDetail) {
        message.error('학습 정보를 찾을 수 없습니다.');
        navigate('/admin/learning');
        return;
      }
      const targetExists = selectedStudents.some((s) => String(s.id) === String(eventDetail.studentId));
      if (!targetExists) {
        message.warning('현재 편집 중인 학생의 학습이 이미 삭제되었습니다.');
        navigate('/admin/learning');
        return;
      }
      const payload = {
        title: values.learningName,
        // 시작일은 수정 불가 → 서버로 기존 시작일 고정 전송
        startDate: dayjs(eventDetail.start).format('YYYY-MM-DD'),
        endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
        backgroundColor: selectedColor,
        borderColor: selectedColor,
        maxUnitsPerDay: values.maxUnitsPerDay,
      };
      await adminLearningScheduleApi.update?.(String(eventId), payload);
      // 유닛 연결 저장
      try {
        const unitLinks = selectedUnitsState.map(u => ({
          unitId: Number(u.id),
          textbookId: u.textbookId ? Number(u.textbookId) : undefined,
        })).filter(link => !Number.isNaN(link.unitId));
        const unitIds = unitLinks.map(link => link.unitId);
        await adminLearningScheduleApi.linkUnits(String(eventId), { unitIds, unitLinks } as any);
      } catch (_) {}
      alert('학습이 수정되었습니다.');
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // 취소는 현재 사용하지 않음

  const onFinish = (values: any) => {
    console.log('Form values:', {
      ...values,
      selectedStudents,
      selectedColor,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
    });
    
    // 실제로는 API 호출
    alert('학습이 등록되었습니다.');
    navigate('/admin/learning');
  };

  // 수정화면에서는 추가 불가

  const groupedUnits = useMemo(() => {
    const map = new Map<string, { name: string; units: Unit[] }>();
    selectedUnitsState.forEach((unit) => {
      const name = unit.textbookName || '교재 미지정';
      if (!map.has(name)) {
        map.set(name, { name, units: [] });
      }
      map.get(name)!.units.push(unit);
    });
    return Array.from(map.values());
  }, [selectedUnitsState]);

  const buildUnitLinkPayload = (units: Unit[]) => {
    const unitLinks = units
      .map((unit) => {
        const unitId = Number(unit.id);
        if (Number.isNaN(unitId)) return null;
        const payload: { unitId: number; textbookId?: number } = { unitId };
        if (unit.textbookId) {
          const textbookId = Number(unit.textbookId);
          if (!Number.isNaN(textbookId)) {
            payload.textbookId = textbookId;
          }
        }
        return payload;
      })
      .filter((link): link is { unitId: number; textbookId?: number } => !!link);
    return {
      unitLinks,
      unitIds: unitLinks.map((link) => link.unitId),
    };
  };

  const handleDeleteItem = async (unitId: string) => {
    if (!eventId) {
      message.error('학습 정보를 확인할 수 없습니다.');
      return;
    }
    const nextUnits = selectedUnitsState.filter((u) => u.id !== unitId);
    if (nextUnits.length === selectedUnitsState.length) return;

    try {
      const payload = buildUnitLinkPayload(nextUnits);
      await adminLearningScheduleApi.linkUnits(String(eventId), payload as any);
      setSelectedUnitsState(nextUnits);
      message.success('유닛을 삭제했습니다.');
    } catch (error) {
      console.error('Failed to remove unit', error);
      message.error('유닛 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleUnitRemove = async (unitId: string | number) => {
    const numericId = Number(unitId);
    if (!Number.isNaN(numericId)) {
      try {
        const r = await adminLearningScheduleApi.hasUnitProgress(String(eventId), numericId);
        const has = (r?.data?.data?.hasProgress ?? r?.data?.hasProgress ?? r?.hasProgress) === true;
        if (has) {
          message.warning('해당 유닛은 학습된 유닛이라서 삭제할 수 없습니다.');
          return;
        }
      } catch (_) {}
    }

    if (selectedUnitsState.length === 1) {
      Modal.confirm({
        title: '해당 유닛까지 삭제할 경우 숙제 전체가 사라집니다. 삭제하시겠습니까?',
        okText: '삭제',
        okType: 'danger',
        cancelText: '취소',
        onOk: async () => {
          try {
            await adminLearningScheduleApi.linkUnits(String(eventId), { unitIds: [], unitLinks: [] } as any);
            await adminLearningScheduleApi.remove(String(eventId));
            message.success('숙제가 삭제되었습니다.');
            navigate('/admin/learning');
          } catch (_) {}
        }
      });
      return;
    }
    await handleDeleteItem(String(unitId));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px' }}>
          <div>로딩 중...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!eventDetail) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px' }}>
          <div>이벤트를 찾을 수 없습니다.</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="small" style={{ width: '100%',  marginBottom: '24px'}}>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/admin/learning')}
              >
                목록으로
              </Button>
            </Space>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                학습 수정
              </Title>
            </div>
          </Space>
        

    {/* 등록 수정 폼 */}
                        
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
        <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  label="학습명"
                  name="learningName"
                  rules={[{ required: true, message: '학습명을 입력해주세요' }]}
                >
                  <Input placeholder="학습명을 입력하세요" size="large" />
                </Form.Item>
              </Col>
            </Row>
            <Card>
            <Row gutter={24} align="bottom">
              <Col span={8}>
                <Form.Item
                  label="유형"
                  name="type"
                >
                  <Select placeholder="유형" size="large" disabled>
                    {typeOptions.map(type => (
                      <Option key={type.id} value={type.id}>
                        {type.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="교재" name="textbook">
                  <Select placeholder="교재" size="large" disabled value={selectedTextbook || undefined}>
                    {textbooks.map(textbook => (
                      <Option key={textbook.id} value={textbook.id}>
                        {textbook.name} ({textbook.publisher})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="유닛" name="unit">
                  <Select placeholder="유닛" disabled size="large" />
                </Form.Item>
              </Col>
            </Row>
            {/* 추가 불가 (기획) */}
            <div style={{ marginBottom: '24px' }}>
              <Text strong>선택된 목록</Text>
              {groupedUnits.length === 0 ? (
                <Table
                  dataSource={[]}
                  columns={[]}
                  pagination={false}
                  size="small"
                  locale={{ emptyText: '선택된 항목이 없습니다.' }}
                />
              ) : (
                groupedUnits.map((group, groupIndex) => (
                  <div key={`${group.name}-${groupIndex}`} style={{ marginTop: groupIndex === 0 ? 0 : 16 }}>
                    <Text>{group.name}</Text>
                    {(() => {
                      const totalUnits = group.units.length;
                      const groupDataSource = group.units.map((unit, idx) => ({
                        key: unit.id,
                        no: totalUnits - idx,
                        type: (unit as any).unitCategory || form.getFieldValue('type') || '-',
                        name: group.name || '-',
                        unitName: unit.name,
                        status: '등록된 항목',
                      }));
                      return (
                        <Table
                          dataSource={groupDataSource}
                          columns={[
                        { title: '순번', dataIndex: 'no', key: 'no', width: 60, align: 'center' },
                        { title: '유형', dataIndex: 'type', key: 'type', width: 200, align: 'center' },
                        { title: '교재', dataIndex: 'name', key: 'name', align: 'center' },
                        { title: '유닛명', dataIndex: 'unitName', key: 'unitName', align: 'center' },
                        { title: '학습상태', dataIndex: 'status', key: 'status', align: 'center' },
                        {
                          title: '삭제',
                          key: 'action',
                          width: 100,
                          align: 'center',
                          render: (_text: string, record: any) => (
                            <Button
                              type="primary"
                              danger
                              size="small"
                              onClick={() => handleUnitRemove(record.key)}
                            >
                              삭제
                            </Button>
                          )
                        },
                          ]}
                          pagination={false}
                          size="small"
                          locale={{ emptyText: '선택된 항목이 없습니다.' }}
                        />
                      );
                    })()}
                  </div>
                ))
              )}
            </div>
            </Card>
            <Row gutter={24}>
                <Col span={8} 
                className='mt-4'
                >
                    <Form.Item
                    label="일일 최대 학습가능한 유닛 개수"
                    name="maxUnitsPerDay"
                    rules={[{ required: true, message: '일일 최대 유닛 개수를 선택해주세요' }]}
                    >
                    <Select placeholder="유닛 개수를 선택하세요" size="large">
                        {maxUnitsPerDayOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                            {option.label}
                        </Option>
                        ))}
                    </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  label="학습 기간"
                  name="dateRange"
                  rules={[{ required: true, message: '학습 기간을 선택해주세요' }]}
                >
                  <RangePicker 
                    style={{ width: '100%' }}
                    placeholder={['시작일', '종료일']}
                    size="large"
                    format="YYYY-MM-DD"
                    disabledDate={(current) => {
                      try {
                        const start = dayjs(eventDetail?.start);
                        return !!current && current.isBefore(start, 'day');
                      } catch { return false; }
                    }}
                    onCalendarChange={(values, _ds, info) => {
                      if (!eventDetail) return;
                      if (info?.range === 'start') {
                        // 시작일 변경 시도 차단
                        message.warning('시작일은 수정할 수 없습니다.');
                        const endVal = values?.[1] ? values?.[1] : (eventDetail.end ? dayjs(eventDetail.end) : dayjs(eventDetail.start));
                        form.setFieldsValue({ dateRange: [dayjs(eventDetail.start), endVal] });
                      }
                    }}
                    onChange={(values) => {
                      if (!eventDetail || !values) return;
                      const fixedStart = dayjs(eventDetail.start);
                      const endVal = values?.[1];
                      // 시작일은 고정, 종료일만 허용(시작 이전은 시작일로 보정)
                      const safeEnd = endVal && endVal.isBefore(fixedStart, 'day') ? fixedStart : endVal;
                      const current = form.getFieldValue('dateRange');
                      const sameStart = current && current[0] && dayjs(current[0]).isSame(fixedStart, 'day');
                      if (!sameStart || (safeEnd && current && current[1] && !dayjs(current[1]).isSame(safeEnd, 'day'))) {
                        form.setFieldsValue({ dateRange: [fixedStart, safeEnd || fixedStart] });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            
              <Col span={12}>
                <Form.Item label="캘린더 색상" name="calendarColor">
                  <ColorPicker value={selectedColor} disabled showText size="large" />
                </Form.Item>
              </Col>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="채점 여부" name="scoring">
                  <Radio.Group size="large" disabled>
                    {scoringOptions.map(option => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            {/* 학습 대상 */}
            <Form.Item label="학습 대상">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="dashed" icon={<PlusOutlined />} style={{ width: '100%' }} size="large" disabled>
                  학습 대상 추가(복사 기능 사용)
                </Button>               
                
                                  {selectedStudents.length > 0 && (
                    <div>
                      <Text strong>선택된 학생 ({selectedStudents.length}명):</Text>
                      <div style={{ marginTop: 8 }}>
                        <StudentTable
                          students={selectedStudents}
                          pagination={false}
                          size="small"
                          type="learning"
                          showRemoveButton={true}
                          onRemoveStudent={async (record: any) => {
                            try {
                              const r = await adminLearningScheduleApi.hasStudentProgress(String(eventId), Number(record.id));
                              const has = (r?.data?.data?.hasProgress ?? r?.data?.hasProgress ?? r?.hasProgress) === true;
                              if (has) { message.warning('학습을 진행한 학생은 제외할 수 없습니다.'); return; }
                            } catch (_) {}
                            try {
                              const academyId = Number(eventDetail?.academy || 0);
                              const title = String(eventDetail?.title || '');
                              const startDate = dayjs(eventDetail?.start).format('YYYY-MM-DD');
                              const endDate = dayjs(eventDetail?.end).format('YYYY-MM-DD');
                              await adminLearningScheduleApi.deleteByStudent({
                                academyId,
                                title,
                                startDate,
                                endDate,
                                studentUserId: Number(record.id),
                                groupUuid: eventDetail?.groupUuid
                              });
                              message.success('해당 학생의 동일 기간 학습이 삭제되었습니다.');
                            } catch (_) {
                              message.error('삭제 중 오류가 발생했습니다.');
                              return;
                            }
                            handleRemoveStudent(record.id);
                          }}
                        />
                      </div>
                    </div>
                  )}
              </Space>
            </Form.Item>
            <Row gutter={24}>
            <Col span={8}>
                <Form.Item
                  label="등록자"
                  name="registrant"
                >
                  <Input disabled size="large" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="등록일"
                  name="registrationDate"
                >
                  <Input disabled size="large" />
                </Form.Item>
              </Col>
            </Row>
            <Divider />

            {/* 버튼 */}
            <Row justify="end">
              <Space>
                <Button onClick={() => navigate('/admin/learning')} size="large">
                  취소
                </Button>
                <Button type="primary" onClick={handleSave} icon={<SaveOutlined />} size="large">
                  저장
                </Button>
              </Space>
            </Row>
          </Form>
    

        {/* 학생 선택 모달 */}
        <Modal
          title="학습 대상 선택"
          open={isStudentModalVisible}
          onOk={handleStudentModalOk}
          onCancel={handleStudentModalCancel}
          width={800}
          footer={[
            <Button key="cancel" onClick={handleStudentModalCancel}>
              취소
            </Button>,
            <Button key="ok" type="primary" onClick={handleStudentModalOk}>
              확인
            </Button>,
          ]}
        >
          <StudentTable
            students={students}
            pagination={false}
            size="small"
            type="learning"
            rowSelection={{
              selectedRowKeys,
              onChange: handleRowSelectionChange,
            }}
          />
          <Pagination
            total={students.length}
            pageSize={10}
            align="center"
            style={{ marginTop: '20px' }}
            current={currentPage}
            onChange={(page) => setCurrentPage(page)}
          />
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminLearningDetail; 